import React from 'react';
import { X, Download, Sparkles, ShieldCheck, ArrowRight, Smartphone, RefreshCw } from 'lucide-react';
import { CkLogo } from './CkLogo';
import { VersionInfo, CURRENT_APP_VERSION, triggerDirectApkDownload } from '../utils/updateChecker';

interface UpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  versionInfo: VersionInfo | null;
}

export const UpdateModal: React.FC<UpdateModalProps> = ({ isOpen, onClose, versionInfo }) => {
  if (!isOpen || !versionInfo) return null;

  const handleUpdate = () => {
    triggerDirectApkDownload(versionInfo.downloadUrl);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-fadeIn">
      <div className="relative glass-card w-full max-w-md p-6 text-center shadow-2xl border border-cyan-500/40 overflow-hidden">
        {/* Glow corner */}
        <div className="absolute -top-20 -right-20 w-48 h-48 bg-cyan-500/20 rounded-full blur-3xl pointer-events-none" />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white rounded-xl hover:bg-white/5 transition-colors"
        >
          <X size={18} />
        </button>

        {/* Top Emblem */}
        <div className="relative inline-block mb-3">
          <CkLogo size={60} className="mx-auto drop-shadow-[0_0_25px_rgba(0,240,255,0.4)]" />
          <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-cyan-500"></span>
          </span>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-extrabold text-white font-display">New Update Available!</h2>
        <div className="inline-flex items-center gap-2 mt-2 px-3 py-1 rounded-full bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 text-xs font-mono font-bold">
          <span>v{CURRENT_APP_VERSION}</span>
          <ArrowRight size={12} />
          <span className="text-emerald-400">v{versionInfo.version}</span>
        </div>

        {/* Release Notes */}
        <div className="my-5 p-4 rounded-2xl bg-black/50 border border-white/10 text-left text-xs text-gray-300 space-y-2">
          <div className="font-bold text-white flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-cyan-400">
            <Sparkles size={13} />
            What's New in v{versionInfo.version}:
          </div>
          <div className="whitespace-pre-line text-gray-300 leading-relaxed font-sans pl-1">
            {versionInfo.releaseNotes || '• Performance enhancements & bug fixes'}
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-2.5">
          <button
            onClick={handleUpdate}
            className="btn-primary w-full py-3.5 text-xs font-bold shadow-lg shadow-cyan-500/25 flex items-center justify-center gap-2"
          >
            <Download size={16} />
            <span>Download &amp; Install Update (APK)</span>
          </button>

          <button
            onClick={onClose}
            className="w-full py-2 text-xs font-semibold text-gray-400 hover:text-white transition-colors"
          >
            Remind Me Later
          </button>
        </div>

        <div className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-slate-500 font-medium">
          <ShieldCheck size={13} className="text-cyan-400" />
          <span>Official Signed Package • Direct In-App Upgrade</span>
        </div>
      </div>
    </div>
  );
};
